import React, { useState, useEffect, useCallback, useMemo } from 'react';
import QRCode from 'react-qr-code';
import {
    Button,
    Col,
    Container,
    Form,
    OverlayTrigger,
    Pagination,
    Row,
    Table,
    Tooltip
} from 'react-bootstrap';
import {
    RiCheckLine,
    RiCloseLine,
    RiDeleteBin2Line,
    RiDownload2Fill,
    RiEditLine,
    RiFileCopyLine
} from 'react-icons/ri';
import { Subscriber, Missionary } from '../../supabase/types';
import {
    deleteSubscriber,
    editSubscriber,
    getSubscribersByMissionary
} from '../../supabase/supabase';
import SubscriptionForm from '../Subscription';
import QRCodeSection from './QRCodeSection';
import SubscriberTable from './SubscriberTable';
import MissionaryDetails from './MissionaryDetails';
import MissionaryImageViewer from './MissionaryImageViewer';
import { StripeForm } from '../StripeForm';

// Separate types into their own declarations
interface DashProps {
    missionary: Missionary;
}

interface EditingState {
    id?: number | null;
    value: string;
}

const MissionaryDashboard: React.FC<DashProps> = ({ missionary }) => {
    // State management with custom hooks
    const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [canEdit, setCanEdit] = useState(false);
    const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
    const [editing, setEditing] = useState<EditingState>({ id: null, value: '' });

    const itemsPerPage = 10;

    // Memoized values
    const missionaryLink = useMemo(
        () => `${window.location.origin}/s/${missionary.unique_id}`,
        [missionary.unique_id]
    );

    const filteredSubscribers = useMemo(
        () =>
            subscribers.filter(
                (subscriber) =>
                    searchText === '' ||
                    subscriber.email.toLowerCase().includes(searchText.toLowerCase())
            ),
        [subscribers, searchText]
    );

    const currentSubscribers = useMemo(() => {
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        return filteredSubscribers.slice(indexOfFirstItem, indexOfLastItem);
    }, [filteredSubscribers, currentPage]);

    // Callbacks
    const fetchSubscribers = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await getSubscribersByMissionary(missionary.unique_id!);
            setSubscribers(data);
        } catch (err) {
            setError('Error loading subscribers');
            console.error('Error fetching subscribers:', err);
        } finally {
            setIsLoading(false);
        }
    }, [missionary.unique_id]);

    const handleEditSave = useCallback(async (subscriber: Subscriber, newEmail: string) => {
        try {
            const updatedSubscriber = { ...subscriber, email: newEmail };
            const { error } = await editSubscriber(updatedSubscriber);
            if (error) throw error;

            setSubscribers((prev) =>
                prev.map((sub) =>
                    sub.subscriber_id === subscriber.subscriber_id
                        ? { ...sub, email: newEmail }
                        : sub
                )
            );
        } catch (err) {
            console.error('Error updating subscriber:', err);
        }
    }, []);

    const handleActiveToggle = useCallback(async (subscriber: Subscriber) => {
        setSubscribers((prev) =>
            prev.map((sub) =>
                sub.subscriber_id === subscriber.subscriber_id
                    ? { ...sub, active: !sub.active }
                    : sub
            )
        );

        try {
            await editSubscriber({ ...subscriber, active: !subscriber.active });
        } catch (err) {
            // Revert on error
            setSubscribers((prev) =>
                prev.map((sub) =>
                    sub.subscriber_id === subscriber.subscriber_id
                        ? { ...sub, active: subscriber.active }
                        : sub
                )
            );
            console.error('Error updating subscriber:', err);
        }
    }, []);

    const handleDelete = useCallback(async (subscriber: Subscriber) => {
        try {
            await deleteSubscriber(subscriber);
            setSubscribers((prev) =>
                prev.filter((s) => s.subscriber_id !== subscriber.subscriber_id)
            );
            // Reset to page 1 if current page becomes empty
            const newTotal = subscribers.length - 1;
            const maxPage = Math.ceil(newTotal / itemsPerPage);
            if (currentPage > maxPage && maxPage > 0) {
                setCurrentPage(maxPage);
            }
        } catch (err) {
            console.error('Error deleting subscriber:', err);
        }
    }, [subscribers.length, currentPage, itemsPerPage]);

    // Effects
    useEffect(() => {
        fetchSubscribers();
    }, [fetchSubscribers]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    if (isLoading) return (
        <div className="d-flex justify-content-center align-items-center" style={{minHeight: '200px'}}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );
    
    if (error) return (
        <div className="alert alert-danger m-4" role="alert">
            <strong>Error:</strong> {error}
        </div>
    );

    return (
        <div className="modern-dashboard">
            <Container fluid className="px-4">
                {/* Top Section - Details and QR Code */}
                <Row className="g-4 mb-4">
                    <Col lg={6}>
                        <div className="dashboard-card">
                            <MissionaryDetails missionary={missionary} />
                            
                            {missionary.subscription_status === 'free' && (
                                <div className="subscription-prompt">
                                    <h5>Ready to get started?</h5>
                                    <p className="text-muted mb-3">Choose a plan to begin forwarding emails</p>
                                    <Button 
                                        className="cta-button"
                                        onClick={() => setShowSubscriptionForm(true)}
                                    >
                                        Pick a Plan
                                    </Button>
                                </div>
                            )}
                            
                            {missionary.subscription_status === 'active' && (
                                <div className="status-active">
                                    <div className="status-badge">
                                        <span className="status-dot"></span>
                                        Active
                                    </div>
                                    <h5>You're all set!</h5>
                                    <p className="mb-2">Send emails to forward them to your subscribers:</p>
                                    <div className="email-address">
                                        forward@pressforwardmail.com
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>
                    
                    <Col lg={6}>
                        <div className="dashboard-card">
                            <QRCodeSection missionaryLink={missionaryLink} />
                        </div>
                    </Col>
                </Row>
                
                {/* Subscribers Section */}
                <Row>
                    <Col>
                        <div className="dashboard-card">
                            {subscribers.length > 0 && (
                                <div className="mb-3">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search subscribers..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        className="search-input"
                                    />
                                </div>
                            )}
                            <SubscriberTable
                                subscribers={currentSubscribers}
                                onSave={handleEditSave}
                                onDelete={handleDelete}
                                onActiveToggle={handleActiveToggle}
                                currentPage={currentPage}
                                totalItems={filteredSubscribers.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </Col>
                </Row>
                
                {/* Images Section */}
                <Row className="mt-4">
                    <Col>
                        <div className="dashboard-card">
                            <MissionaryImageViewer uniqueId={missionary.unique_id!} />
                        </div>
                    </Col>
                </Row>
            </Container>

            <SubscriptionForm
                show={showSubscriptionForm}
                handleClose={() => setShowSubscriptionForm(false)}
                missionaryId={`${missionary.missionary_id}`}
            />
        </div>
    );
};

export default React.memo(MissionaryDashboard);
